import { useState } from "react";
import { toast } from "sonner";
import { Phone, Mail, MapPin, Globe, CheckCircle2 } from "lucide-react";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { PageHeader } from "@/components/shared/PageHeader";
import { FormInput } from "@/components/shared/FormInput";
import { PrimaryButton } from "@/components/shared/Buttons";
import { EnquiryForm } from "@/components/shared/EnquiryForm";
import { useSeo } from "@/hooks/useSeo";
import { CONTACT } from "@/lib/site";
import { submitContactMessage, isValidIndianMobile, isValidEmail } from "@/lib/api";

const msgInitial = { name: "", mobile: "", email: "", subject: "", message: "" };

const ContactMessageForm = () => {
  const [form, setForm] = useState(msgInitial);
  const [errors, setErrors] = useState({});
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);
  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const validate = () => {
    const e = {};
    if (!form.name.trim()) e.name = "Name is required.";
    if (!isValidIndianMobile(form.mobile)) e.mobile = "Enter a valid 10-digit Indian mobile number.";
    if (!isValidEmail(form.email)) e.email = "Enter a valid email address.";
    if (!form.subject.trim()) e.subject = "Subject is required.";
    if (!form.message.trim()) e.message = "Message is required.";
    setErrors(e);
    return Object.keys(e).length === 0;
  };

  const onSubmit = async (ev) => {
    ev.preventDefault();
    if (!validate()) return;
    setSubmitting(true);
    try {
      const res = await submitContactMessage(form);
      toast.success(res.message || "Message sent!");
      setDone(true);
    } catch (err) {
      setErrors(err.errors || {});
      toast.error(err.message || "Failed to send message.");
    } finally {
      setSubmitting(false);
    }
  };

  if (done) {
    return (
      <div data-testid="contact-message-success" className="flex flex-col items-center gap-4 rounded-2xl border border-navy/10 bg-white p-10 text-center">
        <CheckCircle2 className="h-12 w-12 text-orange" />
        <h3 className="font-heading text-2xl text-navy">Message sent!</h3>
        <p className="text-sm text-navy/70">Thanks for reaching out — we&apos;ll get back to you soon.</p>
      </div>
    );
  }

  return (
    <form onSubmit={onSubmit} noValidate data-testid="contact-message-form" className="flex flex-col gap-4 rounded-2xl border border-navy/10 bg-white p-6 sm:p-8">
      <div className="grid gap-4 sm:grid-cols-2">
        <FormInput label="Name" id="cm-name" value={form.name} onChange={set("name")} error={errors.name} placeholder="Your name" testId="cm-name" />
        <FormInput label="Mobile" id="cm-mobile" value={form.mobile} onChange={set("mobile")} error={errors.mobile} placeholder="10-digit mobile" testId="cm-mobile" />
      </div>
      <FormInput label="Email" id="cm-email" type="email" value={form.email} onChange={set("email")} error={errors.email} placeholder="you@example.com" testId="cm-email" />
      <FormInput label="Subject" id="cm-subject" value={form.subject} onChange={set("subject")} error={errors.subject} placeholder="How can we help?" testId="cm-subject" />
      <FormInput label="Message" id="cm-message" as="textarea" value={form.message} onChange={set("message")} error={errors.message} placeholder="Your message" testId="cm-message" />
      <PrimaryButton type="submit" size="lg" disabled={submitting} testId="cm-submit">
        {submitting ? "Sending…" : "Send Message"}
      </PrimaryButton>
    </form>
  );
};

export default function Contact() {
  useSeo("Contact", "Contact ASAI – Atmasamman School of AI. Request a callback or send us a message about programs, internships and college partnerships.");

  return (
    <div data-testid="page-contact">
      <PageHeader
        overline="Contact"
        titleLines={["Let's talk", "about your goals"]}
        description="Request a callback or send us a message — our team is here to help with programs, internships and partnerships."
      />

      <section className="bg-white py-24 md:py-32">
        <div className="container-asai grid gap-14 lg:grid-cols-[0.9fr_1.1fr]">
          <div className="flex flex-col gap-8">
            <h2 className="font-heading text-3xl text-navy">Get in touch directly</h2>
            <div className="flex flex-col gap-5">
              <a href={`tel:+${CONTACT.phoneRaw}`} className="flex items-center gap-4 text-navy/80 hover:text-royal" data-testid="contact-phone">
                <span className="flex h-11 w-11 items-center justify-center rounded-xl bg-royal/10 text-royal"><Phone className="h-5 w-5" /></span>
                {CONTACT.phone}
              </a>
              <a href={`mailto:${CONTACT.email}`} className="flex items-center gap-4 text-navy/80 hover:text-royal" data-testid="contact-email">
                <span className="flex h-11 w-11 items-center justify-center rounded-xl bg-royal/10 text-royal"><Mail className="h-5 w-5" /></span>
                {CONTACT.email}
              </a>
              <div className="flex items-center gap-4 text-navy/80">
                <span className="flex h-11 w-11 items-center justify-center rounded-xl bg-royal/10 text-royal"><Globe className="h-5 w-5" /></span>
                {CONTACT.website}
              </div>
              <div className="flex gap-4 text-navy/80">
                <span className="flex h-11 w-11 shrink-0 items-center justify-center rounded-xl bg-royal/10 text-royal"><MapPin className="h-5 w-5" /></span>
                <address className="not-italic leading-relaxed">
                  {CONTACT.address.map((l) => (<span key={l} className="block">{l}</span>))}
                </address>
              </div>
            </div>
          </div>

          <div>
            <Tabs defaultValue="enquiry" className="w-full">
              <TabsList className="mb-6 grid w-full grid-cols-2" data-testid="contact-tabs">
                <TabsTrigger value="enquiry" data-testid="tab-enquiry">Request a Callback</TabsTrigger>
                <TabsTrigger value="message" data-testid="tab-message">Send a Message</TabsTrigger>
              </TabsList>
              <TabsContent value="enquiry">
                <EnquiryForm source="contact" testId="enquiry-form" />
              </TabsContent>
              <TabsContent value="message">
                <ContactMessageForm />
              </TabsContent>
            </Tabs>
          </div>
        </div>
      </section>
    </div>
  );
}
